import classNames from 'classnames';
import { useRef, useState } from 'react';
import { isEmoji, isStillPlaying, opponentName } from '../../../functions';
import { useCloseOnOutsideOrEscape } from '../../../hooks';
import { IChatMessage } from '../../../Models/LobbyModels';
import { IVisibleOpponent, IVisiblePlayer } from '../../../Models/StateModels';
import Ephemeral from '../Ephemeral/Ephemeral';
import './Dock.css';

export interface IDockProps {
  player: IVisiblePlayer;
  opponents: IVisibleOpponent[];
  pileCount: number;
  lastPlayerMessage: IChatMessage | undefined;
  sendMessage: (message: string) => void;
  pickup: () => void;
}

const REACTIONS = [
  { emoji: '👍', label: 'thumbs up' },
  { emoji: '😄', label: 'big smile' },
  { emoji: '😍', label: 'heart eyes' },
  { emoji: '😮', label: 'wow' },
  { emoji: '💩', label: 'poo' },
  { emoji: '😠', label: 'angry' },
  { emoji: '😢', label: 'cry' },
  { emoji: '🥴', label: 'woozy' },
];

export function isReaction(message: string) {
  return REACTIONS.some(({ emoji }) => emoji === message);
}

const FOCUS_CLASS_NAME =
  'outline-none focus-visible:ring-3 focus-visible:ring-accent-fill';

const TEXT_BUTTON_CLASS_NAME = 'normal-case tracking-normal';

function opponentBefore(player: IVisiblePlayer, opponents: IVisibleOpponent[]) {
  const playerCount = opponents.length + 1;
  for (let step = 1; step < playerCount; step++) {
    const playerIndex = (player.playerIndex - step + playerCount) % playerCount;
    const opponent = opponents.find((o) => o.playerIndex === playerIndex);
    if (opponent && isStillPlaying(opponent)) {
      return opponent;
    }
  }
  return undefined;
}

function yourTurnHint(player: IVisiblePlayer) {
  if (player.handCards.length > 0) {
    return player.canSwap
      ? 'Drag to your table to swap, or the pile to play'
      : 'Tap to select, drag to play';
  }
  if (player.visibleCards.length > 0) {
    return 'Tap to select, drag to play';
  }
  return 'Drag a face-down card to flip it';
}

function turnText(player: IVisiblePlayer, opponents: IVisibleOpponent[]) {
  if (player.isTurn) {
    return { title: 'Your turn', hint: yourTurnHint(player) };
  }

  const opponentPlaying = opponents.find((o) => o.isTurn);
  const title = opponentPlaying
    ? `${opponentName(opponentPlaying)} is playing`
    : 'Waiting';

  if (!isStillPlaying(player)) {
    return { title, hint: "You're out" };
  }

  const previousOpponent = opponentBefore(player, opponents);
  if (!previousOpponent || previousOpponent === opponentPlaying) {
    return { title, hint: "You're next" };
  }
  return { title, hint: `You play after ${opponentName(previousOpponent)}` };
}

export default function Dock({
  player,
  opponents,
  pileCount,
  lastPlayerMessage,
  sendMessage,
  pickup,
}: IDockProps) {
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const reactionRef = useRef<HTMLDivElement>(null);
  const { title, hint } = turnText(player, opponents);
  const mustPickup = player.isTurn && player.mustPickup;
  const canPickup = player.isTurn && player.canPickup && !mustPickup;
  const isHighlighted = player.isTurn && !mustPickup;
  const controlClassName = isHighlighted
    ? 'bg-black/10 text-accent-on outline-none focus-visible:ring-3 focus-visible:ring-accent-on'
    : classNames('bg-control text-ink', FOCUS_CLASS_NAME);

  useCloseOnOutsideOrEscape(reactionRef, isPickerOpen, setIsPickerOpen);

  const sendReaction = (emoji: string) => {
    setIsPickerOpen(false);
    sendMessage(emoji);
  };

  return (
    <div className="mt-auto shrink-0 px-[10px] pb-[20px]">
      <section
        aria-label="Turn"
        className={classNames(
          'flex items-center gap-[10px] rounded-[20px] p-[8px] shadow-dock transition-colors duration-300',
          isHighlighted ? 'bg-accent-fill' : 'bg-surface',
          player.isTurn && 'motion-safe:animate-your-turn'
        )}
      >
        {mustPickup ? (
          <button
            aria-label={`Pick up the pile, ${pileCount} cards`}
            onClick={pickup}
            className={classNames(
              'h-[48px] min-w-0 flex-1 rounded-[14px] bg-danger text-[16px] font-bold text-white',
              TEXT_BUTTON_CLASS_NAME,
              FOCUS_CLASS_NAME
            )}
          >
            Pick up {pileCount}
          </button>
        ) : (
          <div className="flex min-w-0 flex-1 flex-col items-start gap-px pl-4">
            <span
              className={classNames(
                'max-w-full truncate text-[16px] font-bold',
                isHighlighted ? 'text-accent-on' : 'text-ink'
              )}
            >
              {title}
            </span>
            <span
              className={classNames(
                'max-w-full truncate text-[12px] font-medium',
                isHighlighted ? 'text-accent-sub' : 'text-ink-muted'
              )}
            >
              {hint}
            </span>
          </div>
        )}
        {canPickup && (
          <button
            aria-label={`Pick up the pile, ${pileCount} cards`}
            onClick={pickup}
            className={classNames(
              'h-[44px] shrink-0 rounded-[14px] px-[14px] text-[14px] font-semibold',
              controlClassName,
              TEXT_BUTTON_CLASS_NAME
            )}
          >
            Pick up {pileCount}
          </button>
        )}
        <div ref={reactionRef} className="relative shrink-0">
          {isEmoji(lastPlayerMessage?.message) && (
            <Ephemeral
              id={lastPlayerMessage}
              message={lastPlayerMessage?.message}
              classNames="dock-last-message"
            />
          )}
          <button
            aria-label="Send a reaction"
            aria-expanded={isPickerOpen}
            onClick={() => setIsPickerOpen(!isPickerOpen)}
            className={classNames(
              'flex size-[44px] items-center justify-center rounded-[14px]',
              controlClassName
            )}
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="9" />
              <path d="M8.5 14.5a4.5 4.5 0 0 0 7 0M9 9.5v.5M15 9.5v.5" />
            </svg>
          </button>
          {isPickerOpen && (
            <div className="absolute right-0 bottom-full z-10 mb-[8px] grid w-max grid-cols-4 gap-[4px] rounded-[16px] bg-surface p-[6px] shadow-dock">
              {REACTIONS.map(({ emoji, label }) => (
                <button
                  key={emoji}
                  aria-label={label}
                  onClick={() => sendReaction(emoji)}
                  className={classNames(
                    'flex size-[40px] items-center justify-center rounded-[12px] bg-transparent text-[22px] hover:bg-control',
                    FOCUS_CLASS_NAME
                  )}
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
